import { useEffect, useRef } from "react";
import { getSessionId, trackSearchSubmit } from "@/lib/analytics";
import { freezeSearchClickStats } from "@/components/search-click-stats";
import { toAviasalesCode } from "@/lib/airports";

const WL_SRC = "https://tpwidg.com/wl_web/main.js?wl_id=22937";

declare global {
  interface Window {
    __tpwlMounted?: boolean;
  }
}

/**
 * Widget renderuje się w shadow DOM, więc style z styles.css go nie sięgają.
 * Wstrzykujemy arkusz bezpośrednio do shadow rootu: jasne pola, ciemny tekst,
 * koralowy przycisk zgodny z marką.
 */
// Selektory podwójone ([x][x]) — podnosi to specyficzność ponad reguły widgetu.
const SHADOW_CSS = `
[class*="SearchEdit-module__form"][class*="SearchEdit-module__form"],
[class*="DefaultSearch-module__root"][class*="DefaultSearch-module__root"] {
  background-color: #ffffff !important;
  border-radius: 12px !important;
  padding: 6px !important;
}
[class*="Input-module__root"][class*="Input-module__root"],
[class*="Input-module__withSubvalue"][class*="Input-module__withSubvalue"],
[class*="DefaultSearch-module__mergedInputs"][class*="DefaultSearch-module__mergedInputs"],
[class*="DefaultSearch-module__passengersPicker"][class*="DefaultSearch-module__passengersPicker"],
[class*="DefaultSearch-module__flex1"][class*="DefaultSearch-module__flex1"],
[class*="Passengers-module__root"][class*="Passengers-module__root"],
[class*="DateRangePicker-module__mergedInputs"][class*="DateRangePicker-module__mergedInputs"] {
  background-color: #ffffff !important;
  color: #1f1f26 !important;
  border: 1px solid #e1e5ec !important;
  border-radius: 10px !important;
}
[class*="Input-module__input"][class*="Input-module__input"],
[class*="Input-module__valueHint"][class*="Input-module__valueHint"],
[class*="Input-module__subvalue"][class*="Input-module__subvalue"],
[class*="Input-module__button"][class*="Input-module__button"] {
  color: #1f1f26 !important;
  background-color: transparent !important;
}
[class*="Input-module__input"][class*="Input-module__input"]::placeholder {
  color: #9ea9b7 !important;
}
[class*="Input-module__iconContainer"][class*="Input-module__iconContainer"] svg {
  fill: #6b7280 !important;
}
[class*="Button-module__primary"][class*="Button-module__primary"] {
  background-color: #f0564a !important;
  color: #ffffff !important;
  border-radius: 10px !important;
}
[class*="Button-module__primary"][class*="Button-module__primary"]:hover {
  background-color: #d94538 !important;
}
[class*="SearchEdit-module__multiRouteBtn"][class*="SearchEdit-module__multiRouteBtn"] {
  color: #e8eaf0 !important;
}
[class*="SearchEdit-module__multiRouteBtn"][class*="SearchEdit-module__multiRouteBtn"] svg,
[class*="SearchEdit-module__multiRouteBtn"][class*="SearchEdit-module__multiRouteBtn"] svg * {
  fill: #e8eaf0 !important;
  stroke: #e8eaf0 !important;
}
/* Kontrast: za jasne szare napisy w wynikach i filtrach — przyciemniamy. */
[class*="subtitle" i],
[class*="subvalue" i],
[class*="hint" i],
[class*="caption" i],
[class*="muted" i],
[class*="secondary" i],
[class*="duration" i],
[class*="stops" i],
[class*="layover" i],
[class*="transfer" i],
[class*="airport" i],
[class*="date" i]:not(input):not(button),
[class*="city" i],
[class*="filter" i] [class*="price" i],
[class*="Filter" i] [class*="Price" i] {
  color: #4b5563 !important;
}
/* Przełączniki (toggles) — wyraźniejsza obwódka i tło. Tylko same kontrolki,
   nie wiersze filtrów (stąd brak ogólnych selektorów [class*="toggle"]). */
[role="switch"] {
  border: 1px solid #9ca3af !important;
  background-color: #d1d5db !important;
}
[role="switch"][aria-checked="true"] {
  background-color: #f0564a !important;
  border-color: #f0564a !important;
}
`;

/** Czeka aż widget zamontuje shadow root i wstrzykuje do niego style. */
function injectShadowStyles(container: HTMLElement) {
  let attempts = 0;
  const tryInject = () => {
    const sr = container.shadowRoot;
    if (sr) {
      if (!sr.querySelector("style[data-tpwl-override]")) {
        const style = document.createElement("style");
        style.setAttribute("data-tpwl-override", "");
        style.textContent = SHADOW_CSS;
        sr.appendChild(style);
      }
      return;
    }
    if (++attempts < 100) setTimeout(tryInject, 200);
  };
  tryInject();
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function fillPlace(
  sr: ShadowRoot,
  field: "origin" | "destination",
  code: string,
  isCancelled: () => boolean,
): Promise<boolean> {
  const inp = sr.querySelector<HTMLInputElement>(`[data-testid="default-search-${field}-input"]`);
  if (!inp) return false;
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  inp.focus({ preventScroll: true });
  inp.click();
  setter?.call(inp, code);
  inp.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
  const re = new RegExp(`^${code}$`);
  for (let i = 0; i < 50; i++) {
    await sleep(150);
    if (isCancelled()) return false;
    // Podpowiedzi rysują się w osobnym shadow DOM (#tpwl-modals).
    const modals = document.querySelector("#tpwl-modals")?.shadowRoot;
    if (!modals) continue;
    const btn = Array.from(modals.querySelectorAll<HTMLButtonElement>("button")).find((b) => {
      const lines = (b.innerText || "").trim().split("\n");
      return re.test((lines[lines.length - 1] || "").trim());
    });
    if (btn) {
      btn.dispatchEvent(new MouseEvent("mousedown", { bubbles: true, composed: true }));
      btn.click();
      return true;
    }
  }
  return false;
}

async function prefillPlaces(
  host: HTMLElement,
  origin: string,
  destination: string | undefined,
  isCancelled: () => boolean,
) {
  // Czekamy, aż widget narysuje formularz i ustawi domyślne miasto z geolokalizacji
  // (inaczej mogłoby nadpisać nasze ustawienie).
  let sr: ShadowRoot | null = null;
  for (let i = 0; i < 100; i++) {
    if (isCancelled()) return;
    sr = host.shadowRoot;
    const inp = sr?.querySelector<HTMLInputElement>('[data-testid="default-search-origin-input"]');
    if (inp && (inp.value || i > 25)) break;
    await sleep(200);
  }
  if (!sr) return;
  const scrollY = window.scrollY;
  const ok = await fillPlace(sr, "origin", origin.toUpperCase(), isCancelled);
  if (ok && destination) {
    await sleep(300);
    await fillPlace(sr, "destination", destination.toUpperCase(), isCancelled);
  }
  (sr.activeElement as HTMLElement | null)?.blur?.();
  if (Math.abs(window.scrollY - scrollY) > 4) window.scrollTo({ top: scrollY });
}

/**
 * Wyszukiwarka Travelpayouts White Label (wl_id=22937).
 * Główny skrypt jest w <head> (__root.tsx). Przy nawigacji SPA kontenery
 * pojawiają się po wykonaniu skryptu, więc przy kolejnych montażach
 * dołączamy skrypt ponownie (z cache-busterem), aby widget się zainicjował.
 */
export function TravelpayoutsWidget({
  showTickets = true,
  origin,
  destination,
}: {
  showTickets?: boolean;
  origin?: string;
  destination?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Kontenery tworzymy dopiero po hydratacji i dopiero wtedy ładujemy skrypt —
  // inaczej widget wstawiał się przed hydratacją, a React go wymazywał.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // Skrypt widgetu inicjuje się tylko raz na załadowanie strony — przy
    // przejściu wewnątrz serwisu przeładowujemy stronę, żeby się pojawił.
    if (window.__tpwlMounted) {
      window.location.replace(window.location.href);
      return;
    }
    const search = document.createElement("div");
    search.id = "tpwl-search";
    root.appendChild(search);
    let tickets: HTMLDivElement | null = null;
    if (showTickets) {
      tickets = document.createElement("div");
      tickets.id = "tpwl-tickets";
      tickets.className = "mt-6";
      root.appendChild(tickets);
    }
    const s = document.createElement("script");
    s.async = true;
    s.type = "module";
    s.src = WL_SRC;
    document.head.appendChild(s);
    window.__tpwlMounted = true;
    // Widget rysuje się w shadow DOM — style strony go nie sięgają,
    // więc wstrzykujemy własny arkusz do środka (białe pola, koralowy przycisk).
    injectShadowStyles(search);
    if (tickets) injectShadowStyles(tickets);
    // Wstępne ustawienie miast trasy (np. /loty/ktw/stn → KTW → STN).
    // Widget nie ma opcji wypełnienia pól bez uruchamiania wyszukiwania,
    // więc wpisujemy kod lotniska w pole i wybieramy pasującą podpowiedź.
    // Pomijamy, gdy w adresie jest już konkretne wyszukiwanie.
    let cancelled = false;
    // Kody przekładamy na te, które zna Aviasales (WAR → WAW); „cały kraj" pomijamy.
    const o = origin ? toAviasalesCode(origin) : null;
    const d = destination ? toAviasalesCode(destination) ?? undefined : undefined;
    if (o && !new URLSearchParams(window.location.search).has("flightSearch")) {
      void prefillPlaces(search, o, d, () => cancelled);
    }
    return () => {
      cancelled = true;
      search.remove();
      tickets?.remove();
    };
  }, [showTickets, origin, destination]);

  // Zliczanie wyszukiwań. Widget rysuje się w shadow DOM, więc klik nie
  // dociera do nas z prawdziwym celem — zamiast tego obserwujemy parametr
  // `flightSearch`, który widget wpisuje do adresu przy każdym wyszukiwaniu
  // (klik „Szukaj" albo wejście z przycisku „Zobacz oferty").
  // Ten sam wynik w ciągu 10 min (np. odświeżenie strony) liczymy raz.
  useEffect(() => {
    const STORE = "tl_counted_searches";
    const DEDUPE_MS = 10 * 60 * 1000;
    let last = "";
    const check = () => {
      const v = new URLSearchParams(window.location.search).get("flightSearch") ?? "";
      if (!v || v === last) return;
      last = v;
      let seen: Record<string, number> = {};
      try {
        seen = JSON.parse(sessionStorage.getItem(STORE) || "{}");
      } catch {
        seen = {};
      }
      const now = Date.now();
      if (seen[v] && now - seen[v] < DEDUPE_MS) return;
      seen[v] = now;
      try {
        sessionStorage.setItem(STORE, JSON.stringify(seen));
      } catch {
        /* ignore */
      }
      // Najpierw zamrażamy publiczny licznik (odświeżenie dopiero 5 min po tym wyszukiwaniu).
      freezeSearchClickStats();
      trackSearchSubmit();
      logSearchQuery(v);
    };
    check();
    const id = window.setInterval(check, 1000);
    window.addEventListener("popstate", check);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("popstate", check);
    };
  }, []);

  return (
    <div ref={rootRef} className="w-full" suppressHydrationWarning />
  );
}

// Zapis wyszukiwania (trasa + daty) do statystyk admina „Wyszukiwania".
// Format flightSearch: KTW1512STN[2212]1 → KTW→STN, 15.12 (powrót 22.12), 1 os.
function logSearchQuery(v: string) {
  const m = v.toUpperCase().match(/^([A-Z]{3})(\d{2})(\d{2})([A-Z]{3})(?:(\d{2})(\d{2}))?(\d)?/);
  if (!m) return;
  const now = new Date();
  const toDate = (dd: string, mm: string) => {
    let y = now.getFullYear();
    const d = new Date(Date.UTC(y, +mm - 1, +dd));
    if (d.getTime() < now.getTime() - 86_400_000 * 2) d.setUTCFullYear(++y);
    return d.toISOString().slice(0, 10);
  };
  let session: string | null = null;
  try { session = getSessionId(); } catch { /* ignore */ }
  void import("@/integrations/supabase/client").then(({ supabase }) =>
    supabase.from("search_queries").insert({
      session_id: session,
      from_iata: m[1],
      to_iata: m[4],
      depart_date: toDate(m[2], m[3]),
      return_date: m[5] ? toDate(m[5], m[6]) : null,
      pax: m[7] ? Math.min(20, Math.max(1, +m[7])) : 1,
      trip_type: m[5] ? "round" : "oneway",
    }),
  );
}
